import { I18n } from '@aws-amplify/core';

import { BeraResultService } from '@/components/views/BeraJob/services/beraResultService';
import type { TasksRpn } from '@/components/views/BeraJob/services/beraResultService';
import type {
	BeraCustomReportStep,
	BeraJobSummary,
	BeraReportV2,
	BeraReportV2Details,
	BeraReportV2ResultFactor,
	BeraReportV2SelectedFactor,
	BeraReportV2ResultInformations,
	BeraReportV2ResultStep,
	BeraReportV2Scores,
	BeraReportV2StepKeyOptions,
	BeraReportV2StepKeyResult,
	BeraReportV2StepKeyRow
} from '@/types/Bera';

const RESULT_STEP_NAME = 'result';
const INFORMATIONS_STEP_NAME = 'informations';
const NO_TASK_RPN = 0;
const EMPTY_TEXT = '';
const DEFAULT_TASK_TIME_FORMAT = 'seconds';
const PRESENCE_TITLE = 'There is presence of movements';
const NO_PRESENCE_TITLE = 'There is no presence of movements';
const OPENING_QUOTE = '\u201C';
const CLOSING_QUOTE = '\u201D';

const beraResultService = new BeraResultService();

interface BuildResultStepsParams {
	steps: BeraCustomReportStep[];
	stepKeyResults: BeraReportV2StepKeyResult[];
	options: BeraReportV2StepKeyOptions[];
}

interface BuildInformationsParams {
	report: BeraReportV2;
	details: BeraReportV2Details;
	jobSummary: BeraJobSummary;
	organizationName: string;
	companyName: string;
	fileName: string;
}

function findFactor(factors: BeraReportV2ResultFactor[], id: string | null): BeraReportV2SelectedFactor {
	if (!id) {
		return null;
	}

	const result = factors.find((factor) => factor.id === id) ?? null;

	return result;
}

function toStepKeyRow(
	stepKey: BeraCustomReportStep['step_key'][number],
	stepKeyResult: BeraReportV2StepKeyResult | undefined,
	options: BeraReportV2StepKeyOptions | undefined
): BeraReportV2StepKeyRow {
	const stressLevel = findFactor(options?.stressLevels ?? [], stepKeyResult?.stress_level_id ?? null);
	const frequency = findFactor(options?.frequencies ?? [], stepKeyResult?.frequency_id ?? null);
	const totalTaskDuration = findFactor(
		options?.totalTaskDurations ?? [],
		stepKeyResult?.total_task_duration_id ?? null
	);

	const result: BeraReportV2StepKeyRow = {
		id: stepKey.id,
		name: stepKey.name,
		description: stepKey.description,
		hasPresence: !!stepKeyResult?.stress_level_id,
		stressLevel,
		frequency,
		totalTaskDuration,
		jobElement: stepKeyResult?.job_element ?? EMPTY_TEXT,
		taskRpn: stepKeyResult?.task_rpn ?? NO_TASK_RPN
	};

	return result;
}

export function isEvaluationStep(step: BeraCustomReportStep): boolean {
	const result = step.name !== RESULT_STEP_NAME && step.name !== INFORMATIONS_STEP_NAME;

	return result;
}

export function buildResultSteps({ steps, stepKeyResults, options }: BuildResultStepsParams): BeraReportV2ResultStep[] {
	const evaluationSteps = steps.filter(isEvaluationStep);
	const orderedSteps = [...evaluationSteps].sort((first, second) => first.sequence - second.sequence);

	const result = orderedSteps.map((step) => {
		const stepKeys = step.step_key.map((stepKey) => {
			const stepKeyResult = stepKeyResults.find((row) => row.step_key_id === stepKey.id);
			const stepKeyOptions = options.find((item) => item.stepKeyId === stepKey.id);

			return toStepKeyRow(stepKey, stepKeyResult, stepKeyOptions);
		});

		return { id: step.id, name: step.name, description: step.description, stepKeys };
	});

	return result;
}

function collectScores(steps: BeraReportV2ResultStep[]): TasksRpn {
	const rows = steps.flatMap((step) => step.stepKeys);
	const presentRows = rows.filter((row) => row.hasPresence);

	const result: TasksRpn = {
		stress_level: presentRows.flatMap((row) => (row.stressLevel ? [row.stressLevel.score] : [])),
		frequency: presentRows.flatMap((row) => (row.frequency ? [row.frequency.score] : [])),
		total_task_duration: presentRows.flatMap((row) => (row.totalTaskDuration ? [row.totalTaskDuration.score] : []))
	};

	return result;
}

export function buildScores(steps: BeraReportV2ResultStep[], hasKnownInjury: boolean): BeraReportV2Scores {
	const tasksRpn = collectScores(steps);
	const result = beraResultService.calculateRPN(tasksRpn, hasKnownInjury);

	return result;
}

export function buildInformations({
	report,
	details,
	jobSummary,
	organizationName,
	companyName,
	fileName
}: BuildInformationsParams): BeraReportV2ResultInformations {
	const result: BeraReportV2ResultInformations = {
		companyName: organizationName,
		unitName: companyName,
		sectorName: jobSummary.sector_name ?? EMPTY_TEXT,
		lineName: jobSummary.line_name ?? EMPTY_TEXT,
		workstationName: jobSummary.workstation_name ?? EMPTY_TEXT,
		cycleName: jobSummary.cycle_name ?? EMPTY_TEXT,
		taskTime: details.task_time,
		taskTimeFormat: details.task_time_format ?? DEFAULT_TASK_TIME_FORMAT,
		fileName,
		evaluatorName: jobSummary.evaluator_name ?? EMPTY_TEXT,
		workCenter: jobSummary.work_center ?? EMPTY_TEXT,
		taskName: report.task_name,
		operatorEvaluated: jobSummary.operator_evaluated ?? EMPTY_TEXT,
		collectionDate: details.collection_date,
		hasKnownInjury: details.has_known_injury
	};

	return result;
}

export function toStepKeyTitle(step: BeraReportV2ResultStep, stepKey: BeraReportV2StepKeyRow): string {
	let presence = I18n.get(NO_PRESENCE_TITLE);

	if (stepKey.hasPresence) {
		presence = I18n.get(PRESENCE_TITLE);
	}

	const stepName = `${OPENING_QUOTE}${I18n.get(step.description)}${CLOSING_QUOTE}`;

	if (stepKey.description === step.description) {
		const stepTitle = `${presence} ${stepName}`;

		return stepTitle;
	}

	const result = `${presence} ${stepName} - ${I18n.get(stepKey.description)}`;

	return result;
}
